import axios from 'axios';
import ApiService from './api';
import { random } from 'lodash';

/**
 * Two-element `[start, end]` range of moment/dayjs-like objects
 * (must support `.format()`, series endpoints also use `.startOf()/.endOf()/.utcOffset()`).
 * Typed loosely because both moment and dayjs instances are passed in.
 */
type DateRange = any[];

/** Standard `{success, data | error}` envelope used by the product-advisor stats calls. */
interface StatsResult {
  /** Whether the request succeeded */
  success: boolean;
  /** Payload on success (backend `data`, unwrapped when double-nested) */
  data?: any;
  /** The thrown request error on failure */
  error?: any;
}

/** Common filter args for the product-advisor stats endpoints. */
interface ProductAdvisorStatsParams {
  /** Bot to fetch stats for */
  botId: string;
  /** Restrict to a single advisor intent; omitted from the query when falsy */
  intent?: string;
  /** Range start (date string); omitted when falsy */
  from?: string;
  /** Range end (date string); omitted when falsy */
  to?: string;
}

const StatsService = {
  getAnswers: async function (
    botId: string,
    dateRange: DateRange,
    channel?: string | null
  ): Promise<any> {
    try {
      let url =
        '/schaltzentrale/stats/answers/' +
        botId +
        '/' +
        dateRange[0].format('YYYY-MM-DD') +
        '/' +
        dateRange[1].format('YYYY-MM-DD');
      if (channel) {
        url += `/${channel}`;
      }

      const response = await ApiService.get(url);
      if (response.data && response.data.result) {
        return response.data.result;
      }

      return {};
    } catch (error) {
      return {};
    }
  },
  getTakeover: async function (
    botId: string,
    dateRange: DateRange,
    channel?: string | null
  ): Promise<any> {
    try {
      let url =
        '/schaltzentrale/stats/takeoverTypes/' +
        botId +
        '/' +
        dateRange[0].format('YYYY-MM-DD') +
        '/' +
        dateRange[1].format('YYYY-MM-DD');
      if (channel) {
        url += `/${channel}`;
      }
      const response = await ApiService.get(url);
      if (response.data && response.data.result) {
        return response.data.result;
      }

      return {};
    } catch (error) {
      return {};
    }
  },
  getFeedbacks: async function (
    botId: string,
    dateRange: DateRange,
    channel?: string | null
  ): Promise<any> {
    try {
      let url =
        '/schaltzentrale/stats/feedbacks/' +
        botId +
        '/' +
        dateRange[0].format('YYYY-MM-DD') +
        '/' +
        dateRange[1].format('YYYY-MM-DD');
      if (channel) {
        url += `/${channel}`;
      }
      const response = await ApiService.get(url);
      if (response.data && response.data.result) {
        return response.data.result;
      }

      return {};
    } catch (error) {
      return {};
    }
  },
  /**
   * @param kpi KPI endpoint segment, e.g. 'answers' or 'feedbacks'
   */
  getKpi: async function (
    botId: string,
    kpi: string,
    dateRange: DateRange,
    channel?: string | null
  ): Promise<any> {
    try {
      let url = `/schaltzentrale/stats/${kpi}/${botId}/${dateRange[0].format(
        'YYYY-MM-DD'
      )}/${dateRange[1].format('YYYY-MM-DD')}`;

      if (channel) {
        url += `/${channel}`;
      }

      const response = await ApiService.get(url);
      if (response.data && response.data.result) {
        return response.data.result;
      }

      return {};
    } catch (error) {
      return {};
    }
  },
  getSuggestionFrequency: async function (
    botId: string,
    intent: string
  ): Promise<any> {
    try {
      const url = '/schaltzentrale/stats/suggestions/' + botId + '/' + intent;

      const response = await ApiService.get(url);
      if (response.data && response.data.result) {
        return response.data.result;
      }

      return {};
    } catch (error) {
      return {};
    }
  },
  getIntentRelevance: async function (
    botId: string,
    intent: string,
    channel?: string | null
  ): Promise<any> {
    /**
     *
     * hier wird die API für die stats zu einem Intent eines Bots und einem Channel abgefragt
     *
     * - frequence 0 bis 10 von selten bis sehr häufig
     * - history Array der letzten 12 Wochen, frequence Wert pro Woche wie oben 0 selten und 10 Häufig
     *
     * Balken ist voll, wenn der Wert 10 ist, und leer wenn 0.
     *
     * Vorsicht: Skalierung ist genau umgekehrt, als wie sie bisher implementiert ist.
     *
     **/
    const url = '/schaltzentrale/stats/relevance/' + botId + '/' + intent;
    const response = await ApiService.get(url);
    if (!response.data || !response.data.result) {
      return {
        frequent: 0,
        history: [],
        data: 0,
        botId,
        intent,
        channel,
      };
    }

    const data = {
      frequent: response.data.result.frequent,
      history: response.data.result.history,
      data: response.data.result.data,
      botId,
      intent,
      channel,
    };
    // await new Promise(resolve => setTimeout(resolve, 4000)) // Dummy Loading Time
    return data;
  },
  getIntentFeedback: async function (
    botId: string,
    intent: string,
    channel?: string | null
  ): Promise<any> {
    /**
     *
     * hier wird die API für die stats zu einem Intent eines Bots und einem Channel abgefragt
     *
     * - pos: wie häufig wurde das Thema positiv Bewertet. Der Wert ist >= 0
     * - neg: wie häufig wurde das Thema negativ Bewertet. Der Wert ist >= 0
     * - history Array der letzten 12 Wochen, ein Objekt mit pos und neg Bewertungen
     *
     * Progress Balken: Prozentzahl wird ausgerechnet, pos / (pos + neg). 100% ist alles Grün
     *
     **/
    const url = '/schaltzentrale/stats/rating/' + botId + '/' + intent;
    const response = await ApiService.get(url);

    if (!response.data || !response.data.result) {
      return {
        pos: 0,
        history: [],
        neg: 0,
        botId,
        intent,
        channel,
      };
    }

    const dummy = {
      pos: response.data.result.pos,
      neg: response.data.result.neg,
      history: response.data.result.history,
      botId,
      intent,
      channel,
    };
    // await new Promise(resolve => setTimeout(resolve, 4000)) // Dummy Loading Time
    return dummy;
  },
  getIntentAccuracy: async function (
    botId: string,
    intent: string,
    channel?: string | null
  ): Promise<any> {
    const url = '/schaltzentrale/stats/accuracy/' + botId + '/' + intent;
    const response = await ApiService.get(url);

    if (!response.data || !response.data.result) {
      return {
        pos: 0,
        history: [],
        neg: 0,
        botId,
        intent,
        channel,
      };
    }

    const dummy = {
      pos: response.data.result.pos,
      neg: response.data.result.neg,
      history: response.data.result.history,
      botId,
      intent,
      channel,
    };
    return dummy;
  },
  getIntentConversion: async function (
    botId: string,
    intent: string,
    channel?: string | null
  ): Promise<any> {
    /**
     *
     * hier wird die API für die stats zu einem Intent eines Bots und einem Channel abgefragt
     *
     * - goals: wie häufig führte das Thema zur Conversion. Der Wert ist >= 0
     * - views: wie häufig wurde das Thema ausgespielt. Der Wert ist >= 0
     * - history Array der letzten 12 Wochen, ein Objekt mit goals und views
     *
     * Progress Balken: Prozentzahl wird ausgerechnet, goals / (goals + views). 100% ist alles Blau
     *
     **/
    const goals = random(0, 100);
    const dummy = {
      goals,
      views: goals + random(0, 20),
      history: Array.from({ length: 12 }, () => {
        const gls = random(0, 100);
        return { goals: gls, views: gls + random(0, 20) };
      }),
      botId,
      intent,
      channel,
    };
    await new Promise((resolve) => setTimeout(resolve, 2000)); // Dummy Loading Time
    return dummy;
  },
  getConversationSeries: async (
    botId: string,
    dateRange: DateRange,
    channel?: string | null
  ): Promise<any> => {
    const startTime = dateRange[0].startOf('day').utcOffset(0, true).format();
    const endTime = dateRange[1].endOf('day').utcOffset(0, true).format();
    channel = channel ? channel : '';

    const url = `/schaltzentrale/stats/series/conversation/${botId}/${channel}?startTime=${startTime}&endTime=${endTime}`;
    const response = await ApiService.get(url);

    return response?.data;
  },
  getAutomationSeries: async (
    botId: string,
    dateRange: DateRange,
    channel?: string | null
  ): Promise<any> => {
    const startTime = dateRange[0].startOf('day').utcOffset(0, true).format();
    const endTime = dateRange[1].endOf('day').utcOffset(0, true).format();
    channel = channel ? channel : '';

    const url = `/schaltzentrale/stats/series/automation/${botId}/${channel}?startTime=${startTime}&endTime=${endTime}`;
    const response = await ApiService.get(url);

    return response?.data;
  },
  getConversionsSeries: async (
    botId: string,
    dateRange: DateRange,
    channel?: string | null
  ): Promise<any> => {
    const startTime = dateRange[0].startOf('day').utcOffset(0, true).format();
    const endTime = dateRange[1].endOf('day').utcOffset(0, true).format();
    channel = channel ? channel : '';

    const url = `/schaltzentrale/stats/series/conversationgoallead/${botId}/${channel}?startTime=${startTime}&endTime=${endTime}`;
    const response = await ApiService.get(url);

    return response?.data;
  },
  getFeedbackSeries: async (
    botId: string,
    dateRange: DateRange,
    channel?: string | null
  ): Promise<any> => {
    const startTime = dateRange[0].startOf('day').utcOffset(0, true).format();
    const endTime = dateRange[1].endOf('day').utcOffset(0, true).format();

    channel = channel ? channel : '';

    const url = `/schaltzentrale/stats/series/feedback/${botId}/${channel}?startTime=${startTime}&endTime=${endTime}`;
    const response = await ApiService.get(url);

    return response?.data;
  },
  getTakeoversSeries: async (
    botId: string,
    dateRange: DateRange,
    channel?: string | null
  ): Promise<any> => {
    const startTime = dateRange[0].startOf('day').utcOffset(0, true).format();
    const endTime = dateRange[1].endOf('day').utcOffset(0, true).format();
    channel = channel ? channel : '';

    const url = `/schaltzentrale/stats/series/takeovers/${botId}/${channel}?startTime=${startTime}&endTime=${endTime}`;
    const response = await ApiService.get(url);
    return response?.data;
  },
  /**
   * @param k How many top topics to return
   */
  getTopTopics: async (
    botId: string,
    dateRange: DateRange,
    channel?: string | null,
    k: number = 10
  ): Promise<any> => {
    const startTime = dateRange[0].startOf('day').utcOffset(0, true).format();
    const endTime = dateRange[1].endOf('day').utcOffset(0, true).format();
    channel = channel ? channel : '';
    const url = `/schaltzentrale/stats/topktopics/${botId}/${channel}?startTime=${startTime}&endTime=${endTime}&k=${k}`;
    const response = await ApiService.get(url);

    return response?.data;
  },
  getConversationDistribution: async (
    botId: string,
    dateRange: DateRange,
    channel?: string | null,
    intent?: string | null
  ): Promise<any> => {
    const startTime = dateRange[0].startOf('day').utcOffset(0, true).format();
    const endTime = dateRange[1].endOf('day').utcOffset(0, true).format();
    channel = channel ? channel : '';
    // reused as the ready-made query fragment, not the raw intent name
    intent = intent ? `&topic=${intent}` : '';

    const url = `/schaltzentrale/stats/conversation/${botId}/distribution?startTime=${startTime}&endTime=${endTime}${intent}&channel=${channel}`;
    const response = await ApiService.get(url);

    return response?.data;
  },
  getConversationDuration: async (
    botId: string,
    dateRange: DateRange,
    channel?: string | null
  ): Promise<any> => {
    const startTime = dateRange[0].startOf('day').utcOffset(0, true).format();
    const endTime = dateRange[1].endOf('day').utcOffset(0, true).format();
    channel = channel ? channel : '';

    const url = `/schaltzentrale/stats/conversation/${botId}/duration?startTime=${startTime}&endTime=${endTime}&channel=${channel}`;

    const response = await ApiService.get(url);

    return response?.data;
  },
  getConversationTopicAmount: async (
    botId: string,
    dateRange: DateRange,
    channel?: string | null
  ): Promise<any> => {
    const startTime = dateRange[0].startOf('day').utcOffset(0, true).format();
    const endTime = dateRange[1].endOf('day').utcOffset(0, true).format();
    channel = channel ? channel : '';

    const url = `/schaltzentrale/stats/conversation/${botId}/topics?startTime=${startTime}&endTime=${endTime}&channel=${channel}`;

    const response = await ApiService.get(url);

    return response?.data;
  },
  getAnswerSeries: async function (
    botId: string,
    dateRange: DateRange,
    channel: string | null | undefined,
    intent: string
  ): Promise<any> {
    const startTime = dateRange[0].startOf('day').utcOffset(0, true).format();
    const endTime = dateRange[1].endOf('day').utcOffset(0, true).format();

    channel = channel ? channel : '';

    const url = `/schaltzentrale/stats/series/intents/${botId}/${intent}/${channel}?startTime=${startTime}&endTime=${endTime}`;
    const response = await ApiService.get(url);
    return response?.data || [];
  },
  /**
   * Downloads the analysis/protocol export.
   * @returns Binary export data (arraybuffer), or `false` when empty
   */
  getAnalysisExport: async function (
    botId: string,
    dateRange: DateRange
  ): Promise<any> {
    const startTime = dateRange[0].format('YYYY-MM-DD');
    const endTime = dateRange[1].format('YYYY-MM-DD');

    const url = `/schaltzentrale/protocol/${botId}?startDate=${startTime}&endDate=${endTime}`;
    const response = await axios.get(url, { responseType: 'arraybuffer' })

    return response?.data || false;
  },
  /**
   * @param positive When true, exports positive AI feedback; otherwise negative
   * @returns CSV export string, or `false` when empty
   */
  getAIFeedbackExport: async function (
    botId: string,
    dateRange: DateRange,
    positive: boolean
  ): Promise<any> {
    const startTime = dateRange[0].format('YYYY-MM-DD');
    const endTime = dateRange[1].format('YYYY-MM-DD');

    const url = `/schaltzentrale/feedback/${botId}/${positive ? 'pos' : 'neg'}?startDate=${startTime}&endDate=${endTime}&format=csv`;
    const response = await ApiService.get(url);
    return response?.data || false;
  },

  getCSATReport: async function (
    botId: string,
    dateRange: DateRange,
    channel: string | null = null,
  ): Promise<any> {
    const startTime = dateRange[0].format('YYYY-MM-DD');
    const endTime = dateRange[1].format('YYYY-MM-DD');

    let url = `/schaltzentrale/stats/csat/${botId}`;
    if (channel) {
      url += `/${channel}`;
    }

    url += `?startDate=${startTime}&endDate=${endTime}`;
    const response = await ApiService.get(url);
    return response?.data || false;
  },

  productAdvisor: {
    getAmountOfStartedConsultations: async ({
      botId,
      intent,
      from,
      to,
    }: ProductAdvisorStatsParams): Promise<StatsResult> => {
      const url = _generateQueryWithParams({
        baseUrl: `/schaltzentrale/stats/${botId}/product-advisor/started/count`,
        extraParams: { intent, from, to }
      });

      try {
        const response = await ApiService.get(url);
        return { success: true, data: response?.data?.data || response?.data || null };
      } catch (error) {
        return { success: false, error };
      }
    },
    getConversionRate: async ({
      botId,
      intent,
      from,
      to,
    }: ProductAdvisorStatsParams): Promise<StatsResult> => {
      const url = _generateQueryWithParams({
        baseUrl: `/schaltzentrale/stats/${botId}/product-advisor/conversion/rate`,
        extraParams: { intent, from, to }
      });

      try {
        const response = await ApiService.get(url);
        return { success: true, data: response?.data?.data || response?.data || null };
      } catch (error) {
        return { success: false, error };
      }
    },
    getPopularProducts: async ({
      botId,
      intent,
      from,
      to,
      limit,
    }: ProductAdvisorStatsParams & {
      /** Maximum number of products to return */
      limit?: number;
    }): Promise<StatsResult> => {
      const url = _generateQueryWithParams({
        baseUrl: `/schaltzentrale/stats/${botId}/product-advisor/products/popular`,
        extraParams: { intent, from, to, limit }
      });

      try {
        const response = await ApiService.get(url);
        return { success: true, data: response?.data?.data || response?.data || null };
      } catch (error) {
        return { success: false, error };
      }
    },
    getRecommendedProducts: async ({
      botId,
      intent,
      from,
      to,
      limit,
    }: ProductAdvisorStatsParams & {
      /** Maximum number of products to return */
      limit?: number;
    }): Promise<StatsResult> => {
      const url = _generateQueryWithParams({
        baseUrl: `/schaltzentrale/stats/${botId}/product-advisor/products/recommended`,
        extraParams: { intent, from, to, limit }
      });

      try {
        const response = await ApiService.get(url);
        return { success: true, data: response?.data?.data || response?.data || null };
      } catch (error) {
        return { success: false, error };
      }
    },
    getRecommendedProductsAvg: async ({
      botId,
      intent,
      from,
      to,
    }: ProductAdvisorStatsParams): Promise<StatsResult> => {
      const url = _generateQueryWithParams({
        baseUrl: `/schaltzentrale/stats/${botId}/product-advisor/products/recommended/avg`,
        extraParams: { intent, from, to }
      });

      try {
        const response = await ApiService.get(url);
        return { success: true, data: response?.data?.data || response?.data || null };
      } catch (error) {
        return { success: false, error };
      }
    },
    getMostRequestedQueryProperties: async ({
      botId,
      intent,
      from,
      to,
    }: ProductAdvisorStatsParams): Promise<StatsResult> => {
      const url = _generateQueryWithParams({
        baseUrl: `/schaltzentrale/stats/${botId}/product-advisor/products/recommended/query-properties`,
        extraParams: { intent, from, to }
      });

      try {
        const response = await ApiService.get(url);
        return { success: true, data: response?.data?.data || response?.data || null };
      } catch (error) {
        return { success: false, error };
      }
    },
  },
};

/**
 * Private method to generate extra Query params
 * @param param.baseUrl URL without query string
 * @param param.extraParams Object with key - value relation; falsy values are skipped
 */
const _generateQueryWithParams = ({
  baseUrl,
  extraParams,
}: {
  baseUrl: string;
  extraParams: Record<string, any>;
}): string => {
  let url = baseUrl;

  const extraQuery: string[] = [];
  Object.entries(extraParams).forEach(([key, value]) => {
    if (!key || !value || typeof key !== 'string' && typeof value !== 'string') {
      // console.warn('[_generateQueryWithParams] Query parameter skipped, because key or value is empty. Parameters: ', { key, value });
      return;
    }

    extraQuery.push(`${key}=${value}`);
  });

  if (extraQuery.length) url += '?' + extraQuery.join('&');

  return url;
};

export default StatsService;
export { StatsService };
